import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Alert,
} from '@mui/material';
import ShortcutCaptureField from './ShortcutCaptureField';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  DEFAULT_TOOL_SHORTCUTS,
  TOOL_SHORTCUT_LABELS,
  normalizeKeyboardShortcutPreferences,
} from './viewerKeyboardShortcuts';

const ShortcutRow = ({ tool, label, value, onChange }) => (
  <Box className="kairosShortcutEditorRow" sx={{
    display: 'grid', gridTemplateColumns: 'minmax(150px, 1fr) minmax(210px, 0.85fr)', gap: 1.25,
    alignItems: 'center', py: 0.75, borderBottom: '1px solid rgba(110,239,255,0.08)',
  }}>
    <Typography sx={{ color: 'rgba(231,247,255,0.88)', fontSize: 12 }}>{label}</Typography>
    <ShortcutCaptureField
      value={value}
      onChange={(shortcut) => onChange(tool, shortcut)}
      label={label}
      inputProps={{
        'aria-label': `${label} shortcut key`,
        style: { fontFamily: 'monospace', fontWeight: 800 },
      }}
      sx={{
        '& .MuiInputBase-root': { color: '#d7fbff', bgcolor: 'rgba(110,239,255,0.08)', borderRadius: 1.5 },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(110,239,255,0.24)' },
        '& .MuiFormHelperText-root': { color: 'rgba(215,251,255,0.56)', ml: 0, fontSize: 9 },
      }}
    />
  </Box>
);

const rawToolShortcuts = (preferences = {}) => (
  preferences.toolShortcuts && typeof preferences.toolShortcuts === 'object'
    ? { ...DEFAULT_TOOL_SHORTCUTS, ...preferences.toolShortcuts }
    : { ...DEFAULT_TOOL_SHORTCUTS, ...preferences }
);

export default function KeyboardShortcutEditorDialog({ open, preferences, onSave, onReset, onClose }) {
  const [draft, setDraft] = React.useState(() => rawToolShortcuts(preferences));

  React.useEffect(() => {
    if (open) setDraft(rawToolShortcuts(preferences));
  }, [open, preferences]);

  const normalized = normalizeKeyboardShortcutPreferences({ toolShortcuts: draft });
  const hasConflict = normalized.conflicts.length > 0;
  const invalidTools = Object.entries(draft)
    .filter(([tool, key]) => key && normalized.toolShortcuts[tool] !== key)
    .map(([tool]) => TOOL_SHORTCUT_LABELS[tool] || tool);
  const hasInvalid = invalidTools.length > 0;
  const conflictReadout = normalized.conflicts.map(({ key, tools }) => (
    `${key.toUpperCase()}: ${tools.map(tool => TOOL_SHORTCUT_LABELS[tool] || tool).join(' + ')}`
  ));
  const warning = hasConflict
    ? 'Resolve duplicate keys before saving. Each clinical tool must have a unique shortcut.'
    : hasInvalid
      ? 'Use one letter per shortcut. Number keys remain reserved for window/level presets.'
      : 'Saved to your viewer profile for this desktop viewer. These shortcuts still ignore report and text inputs.';

  const update = (tool, key) => setDraft(prev => ({ ...prev, [tool]: key }));
  const reset = () => {
    setDraft(DEFAULT_TOOL_SHORTCUTS);
    if (onReset) onReset(DEFAULT_TOOL_SHORTCUTS);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-describedby="keyboard-shortcut-editor-description" PaperProps={{
      className: 'kairosKeyboardShortcutEditor',
      sx: {
        bgcolor: '#061018', color: '#e7f7ff', border: '1px solid rgba(110,239,255,0.28)', borderRadius: 2.5,
        background: 'linear-gradient(145deg, rgba(5,14,22,0.98), rgba(10,26,38,0.96) 48%, rgba(2,8,14,0.99))',
        boxShadow: '0 28px 80px rgba(0,0,0,0.62), 0 0 42px rgba(0,229,255,0.10)',
      },
    }}>
      <DialogTitle sx={{ display: 'flex', gap: 1.25, alignItems: 'center', borderBottom: '1px solid rgba(110,239,255,0.14)' }}>
        <Box sx={{ width: 30, height: 30, borderRadius: 2, display: 'grid', placeItems: 'center', color: '#6eefff', bgcolor: 'rgba(110,239,255,0.10)', border: '1px solid rgba(110,239,255,0.24)' }}>
          <KeyboardIcon fontSize="small" />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 900, letterSpacing: 1.4, fontFamily: 'monospace' }}>MY VIEWER SHORTCUTS</Typography>
          <Typography id="keyboard-shortcut-editor-description" sx={{ color: 'rgba(215,251,255,0.58)', fontSize: 9, letterSpacing: 0.6, fontFamily: 'monospace' }}>
            Saved to your viewer profile. Shortcuts never fire inside reports or text fields.
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 1.25 }}>
        <Alert severity={hasConflict || hasInvalid ? 'warning' : 'info'} sx={{ mb: 1.25, bgcolor: 'rgba(110,239,255,0.08)', color: '#d7fbff', border: '1px solid rgba(110,239,255,0.16)' }}>
          {warning}
          {conflictReadout.length > 0 && (
            <Box
              className="kairos-shortcut-conflict-readout"
              sx={{ mt: 0.75, fontSize: 11, fontFamily: 'monospace', color: '#ffd166' }}
            >
              Duplicate map: {conflictReadout.join(' · ')}
            </Box>
          )}
          {invalidTools.length > 0 && (
            <Box
              className="kairos-shortcut-invalid-readout"
              sx={{ mt: 0.75, fontSize: 11, fontFamily: 'monospace', color: '#ffd166' }}
            >
              Reserved/invalid: {invalidTools.join(' · ')}
            </Box>
          )}
        </Alert>
        {Object.entries(TOOL_SHORTCUT_LABELS).map(([tool, label]) => (
          <ShortcutRow key={tool} tool={tool} label={label} value={draft[tool]} onChange={update} />
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 1.5, borderTop: '1px solid rgba(110,239,255,0.12)' }}>
        <Button onClick={reset} startIcon={<RestartAltIcon />} sx={{ color: 'rgba(215,251,255,0.66)', fontFamily: 'monospace', fontSize: 11 }}>RESET</Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ color: 'rgba(215,251,255,0.66)', fontFamily: 'monospace', fontSize: 11 }}>CANCEL</Button>
        <Button disabled={hasConflict || hasInvalid} onClick={() => onSave({ toolShortcuts: normalized.toolShortcuts })} variant="outlined" sx={{ color: '#6eefff', borderColor: 'rgba(110,239,255,0.34)', fontFamily: 'monospace', fontSize: 11 }}>SAVE PROFILE</Button>
      </DialogActions>
    </Dialog>
  );
}
